import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ServiceType } from '../types'
import { generateSEOMetadata, updateDocumentHead, SEOMetadata } from '../utils/seo'

interface UseSEOOptions {
  serviceType?: ServiceType
  customTitle?: string
  customDescription?: string
  customMetadata?: Partial<SEOMetadata>
}

export const useSEO = (options: UseSEOOptions = {}) => {
  const location = useLocation()
  
  useEffect(() => {
    const { serviceType, customTitle, customDescription, customMetadata } = options
    
    // Generate base metadata
    const metadata = generateSEOMetadata(serviceType, customTitle, customDescription)
    
    // Apply custom metadata overrides
    const finalMetadata: SEOMetadata = {
      ...metadata,
      ...customMetadata,
      // Ensure canonical URL reflects current location
      canonicalUrl: `https://mernaki.de${location.pathname}`
    }
    
    // Update document head
    updateDocumentHead(finalMetadata)
    
    // Cleanup function to reset title on unmount (optional)
    return () => {
      // We don't reset the title as it should persist until next page load
    }
  }, [location.pathname, options.serviceType, options.customTitle, options.customDescription])
}

// Convenience hooks for specific services
export const useHRSEO = (customTitle?: string, customDescription?: string) => {
  return useSEO({
    serviceType: ServiceType.HR,
    customTitle,
    customDescription
  })
}

export const useComplianceSEO = (customTitle?: string, customDescription?: string) => {
  return useSEO({
    serviceType: ServiceType.COMPLIANCE,
    customTitle,
    customDescription
  })
}

export const useTradeSEO = (customTitle?: string, customDescription?: string) => {
  return useSEO({
    serviceType: ServiceType.TRADE,
    customTitle,
    customDescription
  })
}

export const useIntelligenceSEO = (customTitle?: string, customDescription?: string) => {
  return useSEO({
    serviceType: ServiceType.INTELLIGENCE,
    customTitle,
    customDescription
  })
}

export const useMainPageSEO = (customTitle?: string, customDescription?: string) => {
  return useSEO({
    customTitle,
    customDescription
  })
}